import { toJalali, JALALI_MONTHS } from './jalali';

// Persian number conversion
export const toPersian = (n) =>
  String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

export const fromPersian = (s) =>
  String(s).replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));

// Backend sends send_date/response_date as a Jalali (Persian calendar)
// string shaped like "1405-4-15T17:53:0" (see getJalaliDatetime in
// ticketing_backend/ticketing/DateTimeConverter.py) — not a real ISO
// datetime, so `new Date(str)` can't parse it and yields Invalid Date/NaN.
// This converts that Jalali string to the equivalent Gregorian Date.
const jalaliToGregorian = (jy, jm, jd) => {
  jy += 1595;
  let days = -355668 + 365 * jy + Math.floor(jy / 33) * 8 + Math.floor(((jy % 33) + 3) / 4) + jd
    + (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  let gy = 400 * Math.floor(days / 146097);
  days %= 146097;
  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }
  gy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const gDaysInMonth = [31, (gy % 4 === 0 && (gy % 100 !== 0 || gy % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gm = 0;
  for (; gm < 12 && days >= gDaysInMonth[gm]; gm++) days -= gDaysInMonth[gm];
  return { gy, gm: gm + 1, gd: days + 1 };
};

export const parseBackendDateTime = (str) => {
  const m = /^(\d+)-(\d+)-(\d+)T(\d+):(\d+):(\d+)/.exec(String(str));
  if (!m) return new Date(str);
  const [, jy, jm, jd, h, min, s] = m.map(Number);
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return new Date(gy, gm - 1, gd, h, min, s);
};

const PERSIAN_WEEKDAY = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { weekday: 'long' });

const coerceToDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const parsed = parseBackendDateTime(String(input));
  if (!isNaN(parsed.getTime())) return parsed;
  const fallback = new Date(input);
  return isNaN(fallback.getTime()) ? null : fallback;
};

/** Jalali date: day + month name + year (no weekday). */
export const formatJalaliDate = (input) => {
  const date = coerceToDate(input);
  if (!date) return input ? String(input) : '—';
  const { jy, jm, jd } = toJalali(date);
  return toPersian(`${jd} ${JALALI_MONTHS[jm - 1]} ${jy}`);
};

/** Jalali date: weekday + day + month name + year — always in this order. */
export const formatPersianDateLong = (input = new Date()) => {
  const date = coerceToDate(input);
  if (!date) return input ? String(input) : '—';
  const weekday = PERSIAN_WEEKDAY.format(date);
  const { jy, jm, jd } = toJalali(date);
  return toPersian(`${weekday} ${jd} ${JALALI_MONTHS[jm - 1]} ${jy}`);
};

/** Backend/API datetime string -> Jalali display date (day, month, year). */
export const formatDate = (isoStr) => formatJalaliDate(isoStr);

/** Jalali date + time (weekday, day, month, year, then ساعت HH:MM). */
export const formatPersianDateTime = (input) => {
  const date = coerceToDate(input);
  if (!date) return input ? String(input) : '—';
  const datePart = formatPersianDateLong(date);
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${datePart} ساعت ${toPersian(`${h}:${min}`)}`;
};

const TIME_AGO_MAX_DAYS = 7;

export const timeAgo = (isoStr) => {
  if (!isoStr) return '';
  const d = parseBackendDateTime(isoStr);
  if (isNaN(d)) return '';
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'همین الان';
  if (diff < 3600) return toPersian(Math.floor(diff / 60)) + ' دقیقه پیش';
  if (diff < 86400) return toPersian(Math.floor(diff / 3600)) + ' ساعت پیش';
  const days = Math.floor(diff / 86400);
  if (days > TIME_AGO_MAX_DAYS) return formatJalaliDate(d);
  return toPersian(days) + ' روز پیش';
};
